import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { isApiError } from '../../api/client'
import { keys } from '../../api/queries'
import { startErrorText, useJob, useStartFeedAdd, useToolsInfo, type AnalyzeResult } from '../../api/tools'
import { plural } from '../../lib/format'
import { instagramCode, parseLinks } from '../../lib/links'
import { useViewDraft } from '../../lib/useViewDraft'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'
import { JobProgress, JobResult } from '../tools/JobView'
import { LinksField } from '../tools/LinksField'

const usd = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 3 })

interface AddedDialogProps {
  /** Ссылки из адреса (`?add=`, кнопка «В Залёты» в боте). */
  initial?: string
  onClose: () => void
}

/**
 * «+ Добавить ролик» в «Добавленных»: ссылки на Reels → разбор с транскриптом,
 * без группы и кадров. Подписчиков нового автора снимаем отдельно — для доли
 * просмотров от подписчиков, пока ×N нет. Деньги тратятся только по «Добавить».
 */
export function AddedDialog({ initial = '', onClose: finishClose }: AddedDialogProps) {
  const info = useToolsInfo()
  const start = useStartFeedAdd()
  const qc = useQueryClient()
  const [text, setText, resetText] = useViewDraft('feed.add.text', initial)
  const [jobId, setJobId, resetJob] = useViewDraft<string | null>('feed.add.job', null)
  const onClose = () => { resetText(); resetJob(); finishClose() }
  const job = useJob(jobId)
  const finished = useRef(false)

  const parsed = useMemo(() => parseLinks(text, instagramCode), [text])
  const max = info.data?.analyze_max_urls ?? 20
  const count = parsed.ids.length
  const ready = count > 0 && parsed.bad.length === 0 && count <= max
  const reel = info.data?.analyze_price_usd
  const profile = info.data?.profile_price_usd ?? 0
  const budget = info.data?.budget

  // Готово: «Добавленные» перечитываются. Без замечаний окно закрывается само —
  // новые ролики сверху сетки; с ними остаётся итог: что не добавилось и почему.
  const data = job.data
  useEffect(() => {
    if (!data || data.status !== 'done' || finished.current) return
    finished.current = true
    void qc.invalidateQueries({ queryKey: keys.feedAll })
    const result = data.result as AnalyzeResult
    const saved = result.saved_post_ids?.length ?? 0
    if (saved === 0 || result.failed.length > 0 || (result.notes ?? []).length > 0) return
    toast(`Добавлено: ${saved} ${plural(saved, 'ролик', 'ролика', 'роликов')}`)
    onClose()
  }, [data, qc, onClose])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || start.isPending) return
    start.mutate({ urls: parsed.lines }, { onSuccess: (created) => setJobId(created.id) })
  }

  const running = data?.status === 'queued' || data?.status === 'running'
  const done = data?.status === 'done' || data?.status === 'failed'

  return (
    <Dialog
      title="Добавить в Залёты"
      onClose={onClose}
      wide
      footer={
        jobId ? (
          <Button variant={done ? 'primary' : 'ghost'} onClick={onClose}>
            {done ? 'Готово' : 'Свернуть'}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" form="feed-added" disabled={!ready || start.isPending}>
              {start.isPending && <Spinner size={14} className="text-accent-fg" />}
              Добавить
            </Button>
          </>
        )
      }
    >
      {!jobId ? (
        <form id="feed-added" onSubmit={submit} className="space-y-4">
          <p className="text-sm text-pretty text-muted">
            Вставьте ссылки на Reels из Instagram — по одной на строку. Ролик ляжет в «Добавленные» с цифрами и
            транскриптом. Если автора нет в источниках, вместо ×N будет доля просмотров от подписчиков.
          </p>
          <LinksField
            label="Ссылки на Reels"
            value={text}
            onChange={setText}
            parsed={parsed}
            placeholder={'https://www.instagram.com/reel/…\nhttps://www.instagram.com/reel/…'}
            hint={`По одной ссылке на строку, до ${max}`}
            max={max}
            kindLabel="пост Instagram"
          />
          {count > 0 && reel !== undefined && (
            <p className="text-xs text-pretty text-muted">
              Через Apify — не больше ${usd.format(count * (reel + profile))}: ролик до ${usd.format(reel)}
              {profile > 0 && `, подписчики нового автора — ещё до $${usd.format(profile)}`}. Что уже есть в заводе,
              бесплатно.
              {budget && ` Сегодня потрачено $${usd.format(budget.spent_day)} из $${usd.format(budget.limit_day)}.`}
            </p>
          )}
          {budget && !budget.allowed && (
            <p className="text-xs text-danger">Лимит Apify исчерпан: добавятся только ролики, которые уже есть в заводе.</p>
          )}
          {start.isError && (
            <p className="text-sm text-danger">{startErrorText(isApiError(start.error) ? start.error.detail : null)}</p>
          )}
        </form>
      ) : !data ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : running ? (
        <JobProgress job={data} hint="Окно можно закрыть — ролики появятся в «Добавленных»." />
      ) : (
        <JobResult job={data} beforeOpen={onClose} />
      )}
    </Dialog>
  )
}
